import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { videoPlans } from '../api';
import { SECTIONS } from './videoPlanSections';
import './TeachingsHub.css';

/** "Add to a video" sheet: pick where in the video it goes, then an existing plan or start a new one. */
const AddToPlanSheet = ({ moment, onClose, onAdded }) => {
  const [plans, setPlans] = useState(null);
  const [section, setSection] = useState('explain');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    videoPlans.list()
      .then(res => { if (alive) setPlans(res.data.plans || []); })
      .catch(() => { if (alive) { setPlans([]); setError('Could not load your plans.'); } });
    return () => { alive = false; };
  }, []);

  const addTo = async (plan) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await videoPlans.addItem(plan.id, { momentId: moment.id, section });
      onAdded(plan.title);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add it. Please try again.');
      setBusy(false);
    }
  };

  const createAndAdd = async (e) => {
    e.preventDefault();
    const name = title.trim();
    if (!name || busy) return;
    setBusy(true);
    setError('');
    try {
      const created = await videoPlans.create(name, { skillCategory: moment.themes && moment.themes[0] ? moment.themes[0] : null });
      await videoPlans.addItem(created.data.plan.id, { momentId: moment.id, section });
      onAdded(name);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the plan. Please try again.');
      setBusy(false);
    }
  };

  return createPortal(
    <div className="th-overlay" onClick={() => !busy && onClose()}>
      <div className="th-sheet" role="dialog" aria-label="Add to a video" onClick={(e) => e.stopPropagation()}>
        <div className="th-sheet-top">
          <h3 className="th-sheet-title">Add to a video</h3>
          <button type="button" className="th-x" onClick={onClose} disabled={busy} aria-label="Close">✕</button>
        </div>
        <p className="th-quote">{moment.label ? <strong>{moment.label}: </strong> : null}{moment.text}</p>

        <div className="th-label">Where does it go?</div>
        <div className="th-chips">
          {SECTIONS.map(s => (
            <button key={s.key} type="button" className={`th-chip${section === s.key ? ' is-active' : ''}`} onClick={() => setSection(s.key)}>{s.emoji} {s.short}</button>
          ))}
        </div>

        <div className="th-label">Pick a video</div>
        <div className="th-list">
          {plans === null && <div className="th-muted">Loading…</div>}
          {plans && plans.length === 0 && <div className="th-muted">No videos yet - start your first one below.</div>}
          {plans && plans.map(p => (
            <button key={p.id} type="button" className="th-plan-row" onClick={() => addTo(p)} disabled={busy}>
              <span className="th-plan-name">{p.title}</span>
              <span className="th-plan-meta">{p.itemCount} {p.itemCount === 1 ? 'beat' : 'beats'}</span>
            </button>
          ))}
        </div>

        <form className="th-new" onSubmit={createAndAdd}>
          <input className="th-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New video idea, e.g. “Master the lob”" maxLength={120} aria-label="New video idea" disabled={busy} />
          <button type="submit" className="th-primary" disabled={busy || !title.trim()}>Start</button>
        </form>
        {error && <div className="th-error" role="alert">{error}</div>}
      </div>
    </div>,
    document.body
  );
};

export default AddToPlanSheet;
